/**
 * Pure helper functions for V3 state management.
 * All functions are stateless: they take data and return data, with no side effects.
 */

/**
 * Parse event data that may be a JSON string or already an object.
 * @param {string|object} data - Raw event data
 * @returns {object} Parsed data object
 */
export function parseEventData(data) {
    return typeof data === 'string' ? JSON.parse(data) : data;
}

/**
 * Apply a batch of map cell updates to an existing map array.
 * Uses a Map for O(1) lookups by coordinate key.
 *
 * @param {Array} currentMap - Current flat array of map cells
 * @param {Array} cellUpdates - Array of updated cell objects with x, y properties
 * @returns {Array} New map array with updates applied
 */
export function applyMapCellUpdates(currentMap, cellUpdates) {
    if (!currentMap || !Array.isArray(cellUpdates) || cellUpdates.length === 0) {
        return currentMap;
    }

    const updatesMap = new Map();
    cellUpdates.forEach(cell => {
        const key = `${cell.x}-${cell.y}`;
        updatesMap.set(key, cell);
    });

    return currentMap.map(cell => {
        const key = `${cell.x}-${cell.y}`;
        return updatesMap.has(key) ? updatesMap.get(key) : cell;
    });
}

/**
 * Merge profile patch data into an existing profile.
 *
 * @param {object} currentProfile - Current profile object
 * @param {object} profilePatch - Partial profile data to merge
 * @returns {object} Merged profile
 */
export function mergeProfile(currentProfile, profilePatch) {
    if (!profilePatch) return currentProfile;
    return { ...currentProfile, ...profilePatch };
}

/**
 * Build a region card update with proper handling of nested landWatch
 * and explicit null movementProgress removal.
 *
 * @param {object|null} currentRegionCard - Current region card state
 * @param {object} updates - Region card patch data
 * @returns {object} Updated region card
 */
export function buildRegionCardUpdate(currentRegionCard, updates) {
    // Handle nested landWatch updates
    if (updates.landWatch && typeof updates.landWatch === 'object') {
        if (updates.landWatch.status && updates.landWatch.subStatus !== undefined) {
            // Full landWatch replacement - no merging needed
        } else if (currentRegionCard?.landWatch) {
            updates = {
                ...updates,
                landWatch: {
                    ...currentRegionCard.landWatch,
                    ...updates.landWatch,
                },
            };
        }
    }

    // Merge and handle movementProgress null removal
    const newRegionCard = { ...currentRegionCard, ...updates };
    if ('movementProgress' in updates && updates.movementProgress === null) {
        console.log('[useV3State] Removing movementProgress as it is null');
        delete newRegionCard.movementProgress;
    }

    return newRegionCard;
}

/**
 * Build a full world state object from init/full response data.
 * Used by v3Init and v3Update (full type).
 *
 * @param {object} worldData - World data from server
 * @param {object} [prevState] - Previous state to preserve client-only fields (e.g. timezone from cookie)
 * @returns {object} State object ready to set
 */
export function buildFullWorldState(worldData, prevState) {
    const profile = worldData.profile || {};
    // Preserve timezone from previous state if server didn't include it
    // (server uses omitempty, so empty timezone is omitted from JSON)
    if (!profile.timezone && prevState?.profile?.timezone) {
        profile.timezone = prevState.profile.timezone;
    }
    return {
        profile,
        map: worldData.mapCells || worldData.map || [],
        mapSize: worldData.mapSize,
        regionCard: worldData.regionCard,
        position: worldData.position || { x: 0, y: 0 },
        loading: false,
        error: null,
    };
}
